import { useCallback, useEffect, useMemo, useState } from "react";

import PortOne from "@portone/browser-sdk/v2";
import { Dialog } from "@ui/components/Dialog";
import { useCounters } from "@ui/hooks/useCounters/useCounters";
import { useDeleteElementMutationObserver } from "@ui/hooks/useDeleteElementMutationObserver";
import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import { useHavOverlayConditions } from "@ui/hooks/useHavOverlayConditions";
import { useUser } from "@ui/hooks/useUser";
import { useAuthHrefResolver } from "@ui/providers/AuthRoutingProvider";
import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { ImageBehaviour } from "@jti/core-components/components/ImageBehaviour";
import { toast } from "@jti/core-components/components/Toaster";
import type { VerifyResponse } from "@jti/fe-common";
import {
  PASS_VERIFIED_DATE_OF_BIRTH_COOKIE,
  PASS_VERIFIED_NAME,
  PASS_VERIFIED_PHONE_COOKIE,
  setAgeVerificationUuid,
  setExpiringAgeVerification,
  setPassVerifiedData,
} from "@jti/fe-common";
import { AUTH_ROUTE_PATHS } from "@jti/fe-common/routes/auth-routes";
import { CasProfileApi, getSessionStorage, useAvpActions, usePolling, useQueryClient } from "@jti/rest-api";

import type { AgeVerificationContainerProps } from "./components";
import { ActionButtons, AgeVerificationContainer, BackButton, PopupImage } from "./components";
import type { HardAgeVerificationPopupProps } from "./HardAgeVerificationPopup.types";
import { AGE_VERIFICATION_METHOD, AGE_VERIFICATION_STEPS } from "./HardAgeVerificationPopup.types";
import { saveUserAgeVerificationCookie, useHardAgeVerificationCookie } from "./hooks";

import styles from "./HardAgeVerificationPopup.module.scss";

const { sanitize } = dompurify;

const defaultCookieTtlFallback = 86400;
const defaultQrTtlFallback = 180;
const fallbackCommonError = "Something went wrong. Please try again later.";

const hardAgeVerificationModalId = "hardAgeVerificationModal";
const TRIGGERED_AGE_VERIFICATION = "triggeredAgeVerification";

export const HardAgeVerificationPopup: React.FC<HardAgeVerificationPopupProps> = ({
  externalContent,
  logoSrc,
  title,
  description,
  continueButtonLabel,
  exitButtonLabel,
  legalNotice,
  alt,
  desktopImageSrc,
  tabletImageSrc,
  mobileImageSrc,
  logoAlt,
  exitButtonLink,
  backButtonLabel,
  verificationTitle,
  mobileQrHelper,
  verificationSteps,
  getNewQrButtonLabel,
  ttlOfCookie = defaultCookieTtlFallback,
  qrStaleError,
  qrCommonError = fallbackCommonError,
  qrTtl = defaultQrTtlFallback,
  failedProviderVerificationError = fallbackCommonError,
  backgroundColor,
  backgroundBlur,
  // adding PASS for AV for KOR market
  helperText,
  signInURL,
  ageVerificationMethod = AGE_VERIFICATION_METHOD.DIIA_ASYNC,
  waitingMessage,
  signInButtonLabel,
  tryAgainButtonLabel,
  verificationErrorHelperText,
  missingDataFromAvError = fallbackCommonError,
  portalDomNode,
  // Change phone number props
  isOpened = false,
  isPersonalDataUpdatePending = false,
  isChangePersonalData = false,
  ignoreUrls = [],
  onClose,
  onAgeVerificationSuccess,
}) => {
  const config = useConfig();
  const resolveAuthHref = useAuthHrefResolver();
  const { isUserLoggedIn, user } = useUser();
  useHardAgeVerificationCookie({ avMethod: ageVerificationMethod, cookieTtlInSeconds: Number(ttlOfCookie) });
  const { shouldRenderHAV, shouldTriggerAv, isRegisterPage } = useHavOverlayConditions({
    ignoreUrls,
    ageVerificationMethod,
  });

  const [isModalOpened, setIsModalOpened] = useState(shouldRenderHAV);
  const queryClient = useQueryClient();
  const { startPolling, stopPolling } = usePolling();
  // const { isMedium, isSmall, isLarge } = useCurrentBreakpoint();
  const { breakpoint } = useDeviceBreakpoint();
  const { remainingTime, resetTimer, clearTimer: stopStaleTimer } = useCounters(Number(qrTtl));
  const { initAsyncVerification, asyncVerificationData, asyncVerificationPending, verify } = useAvpActions();

  const [isQrStale, setIsQrStale] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [currentStep, setCurrentStep] = useState<AGE_VERIFICATION_STEPS>(AGE_VERIFICATION_STEPS.DISCLAIMER);

  useDeleteElementMutationObserver({ elementId: hardAgeVerificationModalId, isMutationObserverActive: isModalOpened });

  const isAgeVerificationInProgress = currentStep === AGE_VERIFICATION_STEPS.AGE_VERIFICATION;
  const isDiiaAgeVerification = ageVerificationMethod === AGE_VERIFICATION_METHOD.DIIA_ASYNC;
  const isPassVerification = ageVerificationMethod === AGE_VERIFICATION_METHOD.PASS;
  const isButtonsVisible = !isAgeVerificationInProgress || (isPassVerification && !!errorMessage);
  const isTopBackButtonVisible = isAgeVerificationInProgress && !isPassVerification;
  const isHardAgeVerificationPopupOpened = isChangePersonalData ? isOpened : isModalOpened;
  const isPrimaryButtonVisible = !isChangePersonalData || (isChangePersonalData && isAgeVerificationInProgress);

  const descriptionContent = useMemo(() => {
    if (isPassVerification && isAgeVerificationInProgress) {
      return waitingMessage;
    }
    return description;
  }, [description, isAgeVerificationInProgress, isPassVerification, waitingMessage]);

  const primaryButtonLabel = useMemo(() => {
    if (errorMessage) return backButtonLabel;
    return isPassVerification ? signInButtonLabel : continueButtonLabel;
  }, [backButtonLabel, continueButtonLabel, errorMessage, isPassVerification, signInButtonLabel]);

  const secondaryButtonLabel = useMemo(() => {
    if (errorMessage) return tryAgainButtonLabel;
    return isPassVerification ? continueButtonLabel : exitButtonLabel;
  }, [continueButtonLabel, errorMessage, exitButtonLabel, isPassVerification, tryAgainButtonLabel]);

  const modalImage = useMemo(() => {
    if (breakpoint === "mobile" && mobileImageSrc) {
      return mobileImageSrc;
    }

    if (breakpoint === "tablet" && tabletImageSrc) {
      return tabletImageSrc;
    }

    return desktopImageSrc;
  }, [desktopImageSrc, breakpoint, mobileImageSrc, tabletImageSrc]);

  const handlePassAgeVerification = useCallback(
    (consumer_data: VerifyResponse["consumer_data"]) => {
      if (!consumer_data?.date_of_birth || !consumer_data?.phone || !consumer_data?.name) {
        toast.error(missingDataFromAvError);
        throw new Error(missingDataFromAvError);
      }
      const { date_of_birth, phone, name } = consumer_data;
      setPassVerifiedData(name, PASS_VERIFIED_NAME, Number(ttlOfCookie));
      setPassVerifiedData(phone, PASS_VERIFIED_PHONE_COOKIE, Number(ttlOfCookie));
      setPassVerifiedData(date_of_birth, PASS_VERIFIED_DATE_OF_BIRTH_COOKIE, Number(ttlOfCookie));
    },
    [missingDataFromAvError, ttlOfCookie]
  );

  const handleDiiaAgeVerification = useCallback(
    async (avUuid: string) => {
      if (isUserLoggedIn) {
        const casProfileApi = CasProfileApi.init();
        await casProfileApi.updateAgeVerification(avUuid);
        await casProfileApi.getProfile();
      }
    },
    [isUserLoggedIn]
  );

  const checkVerificationStatus = useCallback(
    async (uuid: string) => {
      try {
        const data = {
          country_code: config.ISO_COUNTRY_CODE,
          method_code: ageVerificationMethod,
          request_uuid: uuid,
        };

        const { is_verified, verification_performed, consumer_data } = await verify(data);

        if (!verification_performed) {
          return;
        }

        if (!is_verified && verification_performed) {
          throw new Error(failedProviderVerificationError);
        }

        if (isPassVerification) {
          handlePassAgeVerification(consumer_data);
        }

        if (isDiiaAgeVerification) {
          await handleDiiaAgeVerification(uuid);
        }

        stopPolling();
        setIsModalOpened(false);
        setAgeVerificationUuid(uuid, Number(ttlOfCookie));
        saveUserAgeVerificationCookie(ageVerificationMethod, Number(ttlOfCookie));
        setExpiringAgeVerification(Number(ttlOfCookie));
        if (isChangePersonalData) {
          onAgeVerificationSuccess?.();
          onClose?.();
        } else {
          window.location.reload();
        }
      } catch (error: any) {
        setErrorMessage(error?.message || qrCommonError);
        stopPolling();
        stopStaleTimer();
      }
    },
    [
      config.ISO_COUNTRY_CODE,
      ageVerificationMethod,
      verify,
      isPassVerification,
      isDiiaAgeVerification,
      stopPolling,
      ttlOfCookie,
      isChangePersonalData,
      failedProviderVerificationError,
      handlePassAgeVerification,
      handleDiiaAgeVerification,
      onAgeVerificationSuccess,
      onClose,
      qrCommonError,
      stopStaleTimer,
    ]
  );

  const getAgeVerificationData = useCallback(
    async (identityVerificationIdFromParams?: string) => {
      try {
        setErrorMessage(undefined);
        const identityVerificationId =
          identityVerificationIdFromParams || `identity-verification-${crypto?.randomUUID()}`;

        if (isPassVerification && !identityVerificationIdFromParams) {
          await PortOne.requestIdentityVerification({
            identityVerificationId,
            storeId: config.PORTONE_STORE_ID,
            channelKey: config.PORTONE_CHANNEL_KEY,
            windowType: {
              mobile: "REDIRECTION",
            },
            redirectUrl: location.href,
          });
        }

        const { uuid } = await initAsyncVerification({
          methodCode: ageVerificationMethod,
          userEmail: user?.email,
          fields: isPassVerification
            ? {
                identity_verification_id: identityVerificationId,
              }
            : undefined,
        });

        resetTimer();
        setIsQrStale(false);
        startPolling(() => checkVerificationStatus(uuid), 5000);
      } catch (error: any) {
        setErrorMessage(error?.message || qrCommonError);
      }
    },
    [
      isPassVerification,
      initAsyncVerification,
      ageVerificationMethod,
      resetTimer,
      startPolling,
      config.PORTONE_STORE_ID,
      config.PORTONE_CHANNEL_KEY,
      checkVerificationStatus,
      qrCommonError,
      user?.email,
    ]
  );

  const triggerAgeVerification = useCallback(() => {
    setCurrentStep(AGE_VERIFICATION_STEPS.AGE_VERIFICATION);
    getAgeVerificationData();
  }, [getAgeVerificationData]);

  const exitFromThePage = () => {
    window.location.href = exitButtonLink ?? "";
    stopPolling();
    stopStaleTimer();
  };

  const handleNavigateToSignIn = useCallback(() => {
    const url = new URL(window.location.href);
    const withoutOriginUrl = url.href.replace(url.origin, "");
    const search = { returnUrl: withoutOriginUrl };
    window.location.href = signInURL
      ? resolveAuthHref({ predefinedHref: signInURL, search })
      : resolveAuthHref({ route: AUTH_ROUTE_PATHS.login, search });
    stopPolling();
    stopStaleTimer();
  }, [resolveAuthHref, signInURL, stopPolling, stopStaleTimer]);

  const handleBackButtonClick = useCallback(() => {
    if (isRegisterPage) {
      handleNavigateToSignIn();
    }

    setErrorMessage(undefined);
    setCurrentStep(AGE_VERIFICATION_STEPS.DISCLAIMER);
    queryClient?.resetQueries({ queryKey: ["initAsyncVerification"] });
    stopPolling();
    stopStaleTimer();
  }, [handleNavigateToSignIn, isRegisterPage, queryClient, stopPolling, stopStaleTimer]);

  const handlePrimaryButtonClick = () => {
    if (errorMessage) {
      handleBackButtonClick();
      return;
    }
    switch (ageVerificationMethod) {
      case AGE_VERIFICATION_METHOD.DIIA_ASYNC:
        triggerAgeVerification();
        break;
      case AGE_VERIFICATION_METHOD.PASS:
        handleNavigateToSignIn();
        break;
    }
  };

  const handleSecondaryButtonClick = () => {
    if (errorMessage) {
      triggerAgeVerification();
      return;
    }
    switch (ageVerificationMethod) {
      case AGE_VERIFICATION_METHOD.DIIA_ASYNC:
        exitFromThePage();
        break;
      case AGE_VERIFICATION_METHOD.PASS:
        triggerAgeVerification();
        break;
    }
  };

  const handleRemoveParamsFromUrl = useCallback(() => {
    const url = window.location.href.split("?")[0];
    window.history.replaceState({}, "", url);
  }, []);

  const renderAvMethodProps: AgeVerificationContainerProps = {
    errorMessage,
    mobileQrHelper,
    verificationSteps: verificationSteps ?? "",
    verificationTitle: verificationTitle ?? "",
    isPassVerification,
    getNewQrButtonLabel: getNewQrButtonLabel ?? "",
    verificationErrorHelperText,
    verificationHelperText: helperText,
    isLoading: asyncVerificationPending || isPersonalDataUpdatePending,
    getNewCode: triggerAgeVerification,
    qrCodeValue: asyncVerificationData?.data.deeplink || "",
    verificationErrorMessage: failedProviderVerificationError,
  };

  useEffect(() => {
    if (remainingTime === 0 && isQrStale === false && !asyncVerificationPending) {
      stopPolling();
      setIsQrStale(true);
      setErrorMessage(qrStaleError);
    }
  }, [remainingTime, isQrStale, asyncVerificationPending, qrStaleError, stopPolling]);

  useEffect(() => {
    if (isPassVerification) {
      const windowParams = new URLSearchParams(window.location.search);
      const identityVerificationId = windowParams.get("identityVerificationId");
      const message = windowParams.get("message");
      if (message) {
        setCurrentStep(AGE_VERIFICATION_STEPS.AGE_VERIFICATION);
        setErrorMessage(message);
        handleRemoveParamsFromUrl();
        return;
      }
      if (identityVerificationId) {
        getAgeVerificationData(identityVerificationId);
        setCurrentStep(AGE_VERIFICATION_STEPS.AGE_VERIFICATION);
        handleRemoveParamsFromUrl();
      }
    }
  }, [isPassVerification, getAgeVerificationData, handleRemoveParamsFromUrl]);

  useEffect(() => {
    setIsModalOpened(shouldRenderHAV);
  }, [shouldRenderHAV]);

  useEffect(() => {
    // sessions storage is used to prevent auto-triggering AV when user is already in the process of AV - used for mobile redirection flow
    const isPassAvAutoTriggered = getSessionStorage().get<boolean>(TRIGGERED_AGE_VERIFICATION, false);
    if (shouldTriggerAv && !isAgeVerificationInProgress && !errorMessage && !isPassAvAutoTriggered) {
      getSessionStorage().set(TRIGGERED_AGE_VERIFICATION, true);
      triggerAgeVerification();
    }
  }, [errorMessage, isAgeVerificationInProgress, shouldTriggerAv, triggerAgeVerification]);

  if (!queryClient) {
    console.error("QueryClient is not provided");
    return null;
  }

  return (
    <Dialog
      externalContent={externalContent}
      isOpened={isHardAgeVerificationPopupOpened}
      closable={isChangePersonalData}
      onClose={onClose}
      dialogClassName={styles.dialogContainer}
      className={classNames(styles.dialog, styles.dialogWrapper)}
      dataTestId="qrAgeVerificationDialog"
      backgroundColor={backgroundColor}
      backgroundBlur={backgroundBlur}
      id={hardAgeVerificationModalId}
      portalDomNode={portalDomNode}
      withContentPadding={false}
    >
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <div className={styles.backButtonContainer}>
            {isTopBackButtonVisible && (
              <BackButton onClick={handleBackButtonClick} buttonLabel={backButtonLabel ?? ""} />
            )}
          </div>
          <div className={styles.content}>
            <div className={styles.logoWrapper}>
              <ImageBehaviour src={logoSrc ?? ""} alt={logoAlt ?? ""} className={styles.logo} />
            </div>

            <h2 className={styles.title}>{title}</h2>

            {descriptionContent && (
              <div
                className={styles.description}
                dangerouslySetInnerHTML={{
                  __html: sanitize(descriptionContent),
                }}
              />
            )}

            {isButtonsVisible && (
              <ActionButtons
                isError={!!errorMessage}
                isPassVerification={isPassVerification}
                primaryButtonLabel={primaryButtonLabel}
                secondaryButtonLabel={secondaryButtonLabel}
                isPrimaryButtonVisible={isPrimaryButtonVisible}
                onPrimaryButtonClick={handlePrimaryButtonClick}
                onSecondaryButtonClick={handleSecondaryButtonClick}
              />
            )}

            {breakpoint !== "desktop" && isAgeVerificationInProgress && (
              <AgeVerificationContainer {...renderAvMethodProps} isPortable={true} />
            )}

            {legalNotice && (
              <div className={styles.legalNotice}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: sanitize(legalNotice),
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <div className={styles.functionalContainer}>
          <PopupImage
            imageSrc={modalImage ?? ""}
            alt={alt ?? ""}
            isAgeVerificationInProgress={breakpoint === "desktop" && isAgeVerificationInProgress}
            dataTestId="disclaimerImage"
          />

          {isAgeVerificationInProgress && breakpoint === "desktop" && (
            <AgeVerificationContainer {...renderAvMethodProps} isOverlay={true} />
          )}
        </div>
      </div>
    </Dialog>
  );
};
